import type { CalendarDataProps } from '@eventbrite/timed-entry';
import { format } from 'date-fns';
import type {
    DateAvailabilityStatus,
    ScrollableCalendarData,
    TransformedEventDate,
} from '../../../state/infiniteCalendarSeries';
import { DateFormatType } from '../types';

const longDateFormat: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
};

export const mapEventDatesToCalendar = (
    eventData: Record<string, TransformedEventDate[]>,
    previousCalendarData?: ScrollableCalendarData,
): CalendarDataProps<DateAvailabilityStatus> => {
    const calendarData: CalendarDataProps<DateAvailabilityStatus> =
        previousCalendarData || {
            years: {},
        };
    calendarData.years ||= {};

    for (const [monthYearKey, events] of Object.entries(eventData)) {
        const [yearStr, monthStr] = monthYearKey.split('_');
        const year = +yearStr;
        const month = +monthStr - 1;

        const yearData = (calendarData.years[year] ||= { months: {} });
        const monthData = (yearData.months[month] ||= {
            dates: {},
        });
        monthData.isLoading = false;

        for (const event of events) {
            const date = new Date(event.date).getDate();

            monthData.dates[date] ||= {
                dateCellKey: event.availability as DateAvailabilityStatus,
                dateString: event.date,
            };
        }
    }

    return calendarData;
};

export const getLocaleFormattedDate = (dateString: Date, locale: string) => {
    const parsedDate =
        dateString instanceof Date ? dateString : new Date(dateString);
    if (isNaN(parsedDate.getTime())) return '';
    return parsedDate.toLocaleDateString(locale, longDateFormat);
};

export const getYearDate = (dateString: string) => {
    return format(new Date(dateString), 'yyyy-MM-dd');
};

export const formatDate = (
    dateString: Date,
    formatType: DateFormatType,
    locale: string,
) => {
    if (!dateString) return '';
    return formatType === DateFormatType.long
        ? getLocaleFormattedDate(dateString, locale)
        : getYearDate(dateString.toISOString());
};

export const initializeYearMonthData = (
    prevYears: NonNullable<CalendarDataProps<DateAvailabilityStatus>['years']>,
    year: number,
    month: number,
) => {
    const years = { ...prevYears };
    years[year] = {
        ...years[year],
        months: {
            ...(years[year]?.months || {}),
            [month]: years[year]?.months?.[month] || {
                isLoading: true,
                dates: {},
            },
        },
    };

    return years;
};
